import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import type { ScriptReplaceMark } from "@/services/make-generate";
import { trimToWordCount } from "@/utils/text-word-util";
import {
  getScriptReplaceReadingForRange,
  reconcileScriptReplaceMarks,
  removeScriptReplaceMark,
} from "@/utils/script-utils";
import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { ScriptPronunciationModal } from "./script-pronunciation-modal";
import { SpeakVoiceIcon } from "../agent-creation/assets/speak-voice-icon";
import { EditUnderlineIcon } from "../agent-creation/assets/edit-underline-icon";

// 划词最大字符数
const MAX_WORD_SELECTION_CHARS = 50;

// 划词标记类名
const MARK_CLASS =
  "script-pronunciation-mark inline rounded-[4px] bg-[#f2edff] px-px text-[#7542f7] cursor-pointer align-baseline";

// 敏感词文本颜色
const RISK_WORD_TEXT_COLOR = "rgba(247,144,9,1)";

// 合并区间
function mergeIntervals(intervals: [number, number][]): [number, number][] {
  if (intervals.length === 0) return [];
  const sorted = [...intervals].sort((a, b) => a[0] - b[0]);
  const out: [number, number][] = [];
  let [cs, ce] = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    const [s, e] = sorted[i];
    if (s <= ce) ce = Math.max(ce, e);
    else {
      out.push([cs, ce]);
      cs = s;
      ce = e;
    }
  }
  out.push([cs, ce]);
  return out;
}

// 收集敏感词区间
function collectRiskIntervals(text: string, riskWords: string[]): [number, number][] {
  const hits: [number, number][] = [];
  for (const w of riskWords) {
    if (!w) continue;
    let from = 0;
    while (from <= text.length - w.length) {
      const i = text.indexOf(w, from);
      if (i === -1) break;
      hits.push([i, i + w.length]);
      from = i + 1;
    }
  }
  return mergeIntervals(hits);
}

// 分割区间
function splitSegmentWithRisk(
  start: number,
  end: number,
  riskIntervals: [number, number][]
): { start: number; end: number; isRisk: boolean }[] {
  if (start >= end) return [];
  const parts: { start: number; end: number; isRisk: boolean }[] = [];
  let cur = start;
  for (const [rs, re] of riskIntervals) {
    if (re <= start || rs >= end) continue;
    const s = Math.max(rs, start, cur);
    const e = Math.min(re, end);
    if (e <= s) continue;
    if (s > cur) parts.push({ start: cur, end: s, isRisk: false });
    parts.push({ start: s, end: e, isRisk: true });
    cur = e;
  }
  if (cur < end) parts.push({ start: cur, end, isRisk: false });
  return parts;
}

// 添加文本区间
function appendTextRangeWithRisk(
  parent: HTMLElement,
  text: string,
  start: number,
  end: number,
  riskIntervals: [number, number][]
): void {
  for (const seg of splitSegmentWithRisk(start, end, riskIntervals)) {
    const chunk = text.slice(seg.start, seg.end);
    if (!chunk) continue;
    if (seg.isRisk) {
      const riskSpan = document.createElement("span");
      riskSpan.setAttribute("data-risk-word", "1");
      riskSpan.style.color = RISK_WORD_TEXT_COLOR;
      riskSpan.appendChild(document.createTextNode(chunk));
      parent.appendChild(riskSpan);
    } else {
      parent.appendChild(document.createTextNode(chunk));
    }
  }
}

// 获取光标偏移量
function getCaretOffset(container: HTMLElement): number {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return 0;
  const range = sel.getRangeAt(0);
  if (!container.contains(range.commonAncestorContainer)) return 0;
  const pre = document.createRange();
  pre.selectNodeContents(container);
  pre.setEnd(range.startContainer, range.startOffset);
  return pre.toString().length;
}

// 获取选区偏移量
function getSelectionCharOffsets(container: HTMLElement): { start: number; end: number } | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;
  const range = sel.getRangeAt(0);
  if (!container.contains(range.commonAncestorContainer)) return null;
  const preStart = document.createRange();
  preStart.selectNodeContents(container);
  preStart.setEnd(range.startContainer, range.startOffset);
  const preEnd = document.createRange();
  preEnd.selectNodeContents(container);
  preEnd.setEnd(range.endContainer, range.endOffset);
  const a = preStart.toString().length;
  const b = preEnd.toString().length;
  return { start: Math.min(a, b), end: Math.max(a, b) };
}

// 设置光标偏移量
function setCaretOffset(container: HTMLElement, offset: number): void {
  const sel = window.getSelection();
  if (!sel) return;
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let remaining = offset;
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const len = node.textContent?.length ?? 0;
    if (remaining <= len) {
      const r = document.createRange();
      r.setStart(node, Math.max(0, remaining));
      r.collapse(true);
      sel.removeAllRanges();
      sel.addRange(r);
      return;
    }
    remaining -= len;
  }
  const r = document.createRange();
  r.selectNodeContents(container);
  r.collapse(false);
  sel.removeAllRanges();
  sel.addRange(r);
}

// 获取编辑器纯文本
// 浏览器在 contentEditable 为空时会插入 <br> 导致 innerText 末尾多出 "\n"，需去除
function getEditorPlainText(el: HTMLElement): string {
  return el.innerText.replace(/\r\n/g, "\n").replace(/\u00a0/g, " ").replace(/\n$/, "");
}

// 划词气泡 key
function pronunciationHoverTipKey(tip: HoverTipState): string {
  const [a, b] = tip.mark.indexRange;
  return `script-pronunciation-hover-tip-${a}-${b}`;
}

// 划词工具条 key
function pronunciationToolbarKey(toolbar: { start: number; end: number }): string {
  return `script-pronunciation-toolbar-${toolbar.start}-${toolbar.end}`;
}

// 仅用于「mark.indexRange（闭区间 [start,end]）」转「半开 [start,end+1)」用于渲染 / slice
function toHalfOpenRange(closedRange: [number, number]): [number, number] {
  let [a, b] = closedRange;
  if (a > b) [a, b] = [b, a];
  return [a, b + 1];
}

// 仅用于「半开 [start,end)（来自选区 / modalRange）」转「闭区间 [start,end-1] 用于持久化存储」
function toStoredRange(halfOpenStart: number, halfOpenEnd: number): [number, number] {
  return [halfOpenStart, halfOpenEnd - 1];
}

type ScriptMarkedEditorProps = {
  value: string;
  marks: ScriptReplaceMark[];
  /** 敏感词：命中片段使用橙色字；与划词读音重叠时保留读音背景色，字色以敏感词为准 */
  riskWords?: string[];
  onChange: (text: string) => void;
  onMarksChange: (marks: ScriptReplaceMark[]) => void;
  className?: string;
  maxLineHeight?: number;
  maxCount?: number;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  autoFocus?: boolean;
  readOnly?: boolean;
  upsertMark: (marks: ScriptReplaceMark[], mark: ScriptReplaceMark) => ScriptReplaceMark[];
};

type HoverTipState = {
  left: number;
  top: number;
  reading: string;
  mark: ScriptReplaceMark;
};

/** 口播划词修改读音编辑器 */
export const ScriptMarkedEditor = forwardRef<HTMLDivElement, ScriptMarkedEditorProps>(({
  value,
  marks,
  riskWords = [],
  onChange,
  onMarksChange,
  className,
  maxLineHeight = 5,
  maxCount = 2400,
  onKeyDown,
  autoFocus = true,
  readOnly = false,
  upsertMark,
},
  ref
) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const editorRef = useRef<HTMLDivElement>(null);
  const pendingCaretRef = useRef<number | null>(null);
  const composingRef = useRef(false);
  const [isComposing, setIsComposing] = useState(false);
  const hoverClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [toolbar, setToolbar] = useState<{
    left: number;
    top: number;
    start: number;
    end: number;
  } | null>(null);
  const [hoverTip, setHoverTip] = useState<HoverTipState | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRange, setModalRange] = useState<{ start: number; end: number } | null>(null);
  const [modalReading, setModalReading] = useState("");

  // 调整编辑器高度
  const adjustHeight = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const lineHeight = 24;
    const minHeight = lineHeight;
    const maxHeight = lineHeight * maxLineHeight;
    el.style.height = "";
    if (el.scrollHeight > el.clientHeight) {
      el.style.height = `${Math.max(minHeight, Math.min(el.scrollHeight, maxHeight))}px`;
    } else {
      el.style.height = `${Math.max(minHeight, el.scrollHeight)}px`;
    }
  }, [maxLineHeight]);

  // 清除划词气泡
  const clearHoverSoon = useCallback(() => {
    if (hoverClearTimerRef.current) clearTimeout(hoverClearTimerRef.current);
    hoverClearTimerRef.current = setTimeout(() => {
      setHoverTip(null);
      hoverClearTimerRef.current = null;
    }, 120);
  }, []);

  // 取消划词气泡
  const cancelHoverClear = useCallback(() => {
    if (hoverClearTimerRef.current) {
      clearTimeout(hoverClearTimerRef.current);
      hoverClearTimerRef.current = null;
    }
  }, []);

  // 关闭划词气泡
  const closeHoverTip = useCallback(() => {
    if (hoverClearTimerRef.current) {
      clearTimeout(hoverClearTimerRef.current);
      hoverClearTimerRef.current = null;
    }
    setHoverTip(null);
  }, []);

  // 打开划词气泡
  const openModalForRange = useCallback((start: number, end: number, reading: string) => {
    setModalRange({ start, end });
    setModalReading(reading);
    setModalOpen(true);
    setToolbar(null);
    closeHoverTip();
  }, [closeHoverTip]);

  // 用纯 DOM 构建内容，避免 React 子节点与 contenteditable 冲突 
  const rebuildEditorDom = useCallback((el: HTMLElement, text: string, list: ScriptReplaceMark[], words: string[]) => {
    el.textContent = "";
    const riskIntervals = collectRiskIntervals(text, words);
    const sorted = [...list].sort((a, b) => a.indexRange[0] - b.indexRange[0]);
    let idx = 0;
    for (const m of sorted) {
      const [s, e] = toHalfOpenRange(m.indexRange);
      if (e <= s || s >= text.length) continue;
      const ss = Math.max(0, s);
      const ee = Math.min(e, text.length);
      if (ss > idx) {
        appendTextRangeWithRisk(el, text, idx, ss, riskIntervals);
      }
      const span = document.createElement("span");
      span.setAttribute("class", MARK_CLASS);
      span.setAttribute("data-pronunciation-mark", "1");
      appendTextRangeWithRisk(span, text, ss, ee, riskIntervals);

      span.addEventListener("mouseenter", () => {
        cancelHoverClear();
        const r = span.getBoundingClientRect();
        setHoverTip({
          left: r.left + r.width / 2,
          top: r.top,
          reading: m.text,
          mark: m,
        });
      });
      span.addEventListener("mouseleave", () => {
        clearHoverSoon();
      });
      span.addEventListener("dblclick", (ev) => {
        ev.preventDefault();
        const [a, b] = toHalfOpenRange(m.indexRange);
        openModalForRange(a, b, m.text);
      });

      el.appendChild(span);
      idx = ee;
    }
    if (idx < text.length) {
      appendTextRangeWithRisk(el, text, idx, text.length, riskIntervals);
    }
  },
    [cancelHoverClear, clearHoverSoon, openModalForRange]
  );

  // 更新编辑器 DOM
  useLayoutEffect(() => {
    const el = editorRef.current;
    if (!el || composingRef.current) return;
    const preserved =
      pendingCaretRef.current !== null ? pendingCaretRef.current : getCaretOffset(el);
    pendingCaretRef.current = null;
    rebuildEditorDom(el, value, marks, riskWords);
    const nextCaret = Math.min(Math.max(0, preserved), value.length);
    setCaretOffset(el, nextCaret);
  }, [value, marks, riskWords, rebuildEditorDom]);

  // 调整编辑器高度
  useEffect(() => {
    requestAnimationFrame(adjustHeight);
  }, [value, adjustHeight]);

  // 划词区被删或 mark 被 reconcile 后，原 span 可能不触发 mouseleave，需同步清掉浮层
  useEffect(() => {
    setHoverTip((prev) => {
      if (!prev) return null;
      const [a, b] = toHalfOpenRange(prev.mark.indexRange);
      if (b <= a || a >= value.length) return null;
      const still = marks.some(
        (m) =>
          toHalfOpenRange(m.indexRange)[0] === a &&
          toHalfOpenRange(m.indexRange)[1] === b &&
          m.text === prev.mark.text
      );
      return still ? prev : null;
    });
  }, [value, marks]);

  // 自动聚焦
  useEffect(() => {
    if (autoFocus && editorRef.current) {
      const tmr = setTimeout(() => {
        editorRef.current?.focus();
      }, 100);
      return () => clearTimeout(tmr);
    }
  }, [autoFocus]);

  // 关闭划词工具条
  const closeToolbar = useCallback(() => setToolbar(null), []);

  // 更新划词工具条
  const updateToolbarFromSelection = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const sel = getSelectionCharOffsets(el);
    if (!sel || sel.end <= sel.start) {
      closeToolbar();
      return;
    }
    const slice = value.slice(sel.start, sel.end);
    if (!slice.trim()) {
      closeToolbar();
      return;
    }
    const s = window.getSelection();
    if (!s || s.rangeCount === 0) return;
    try {
      const r = s.getRangeAt(0);
      const rect = r.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        closeToolbar();
        return;
      }
      // 选区跨越编辑器可见区域之外（如全选时滚动到底部，首行已被滚动到编辑器顶部之上）：
      // 直接把工具条吸附到编辑器整体上方，避免出现「工具条飞到页面上面」的视觉割裂。
      const editorRect = el.getBoundingClientRect();
      const selectionAboveEditor = rect.top < editorRect.top;
      const anchorLeft = selectionAboveEditor
        ? editorRect.left + editorRect.width / 2
        : rect.left + rect.width / 2;
      const anchorTop = selectionAboveEditor ? editorRect.top : rect.top;
      setToolbar({
        left: anchorLeft,
        top: anchorTop,
        start: sel.start,
        end: sel.end,
      });
    } catch {
      closeToolbar();
    }
  }, [value, closeToolbar]);

  // 监听文档点击事件
  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      const el = editorRef.current;
      const target = e.target as Node;
      if (toolbar && el && !el.contains(target)) {
        const btn = (target as HTMLElement).closest?.("[data-pronunciation-toolbar]");
        if (!btn) closeToolbar();
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [toolbar, closeToolbar]);

  // 处理输入
  const handleInput = () => {
    if (readOnly) return;
    if (composingRef.current) return;
    const el = editorRef.current;
    if (!el) return;
    const prev = value;
    const plain = trimToWordCount(getEditorPlainText(el), maxCount);
    const caret = getCaretOffset(el);
    const limitedCaret = Math.min(caret, plain.length);
    pendingCaretRef.current = limitedCaret;

    // contentEditable 在“已达上限继续输入”时，可能出现值未变化但 DOM 已变化的情况。
    // 这时 React 不会重渲染，需要主动回滚 DOM，避免视觉上还能继续输入。
    if (plain === value) {
      rebuildEditorDom(el, value, marks, riskWords);
      setCaretOffset(el, Math.min(limitedCaret, value.length));
      closeToolbar();
      closeHoverTip();
      return;
    }

    if (marks.length > 0) {
      onMarksChange(reconcileScriptReplaceMarks(prev, plain, marks, limitedCaret));
    }
    onChange(plain);
    closeToolbar();
    closeHoverTip();
  };

  // 保存划词读音（modalRange 为半开 [start,end)，转闭区间存储）
  const handleSavePronunciation = (reading: string) => {
    if (!modalRange) return;
    const mark: ScriptReplaceMark = {
      type: "replace",
      indexRange: toStoredRange(modalRange.start, modalRange.end),
      text: reading,
    };
    onMarksChange(upsertMark(marks, mark));
    setModalOpen(false);
    setModalRange(null);
  };

  // 移除划词读音（modalRange 为半开 [start,end)，转闭区间匹配存储）
  const handleRemovePronunciation = useCallback(() => {
    if (!modalRange) return;
    onMarksChange(
      removeScriptReplaceMark(marks, toStoredRange(modalRange.start, modalRange.end))
    );
  }, [modalRange, marks, onMarksChange]);

  // 合并 ref
  const mergedRef = (node: HTMLDivElement | null) => {
    editorRef.current = node;
    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  // 当 toolbar 选区与 hoverTip 的 mark 区间有任意重叠时，优先展示 toolbar，隐藏 hoverTip
  const toolbarOverlapsHoverTip = useMemo(() => {
    if (!toolbar || !hoverTip) return false;
    const [a, b] = toHalfOpenRange(hoverTip.mark.indexRange);
    return toolbar.start < b && toolbar.end > a;
  }, [toolbar, hoverTip]);

  return (
    <div className="relative w-full">
      {!value && !isComposing && !readOnly && (
        <span
          aria-hidden
          className="pointer-events-none absolute left-0 top-0 px-4 text-sm leading-[24px] tracking-[0.8px] text-[#98A2B3] select-none indent-[calc(var(--script-text-start,79px)-16px)]"
        >
          {t('live_script.placeholder')}
        </span>
      )}
      <div
        ref={mergedRef}
        contentEditable={!readOnly}
        suppressContentEditableWarning
        spellCheck={false}
        className={cn(
          "overflow-y-auto w-full min-h-[24px] px-4 border-0 outline-none text-sm leading-[24px] tracking-[0.8px] whitespace-pre-wrap break-words",
          "mb-[6.4px]",
          className
        )}
        onInput={handleInput}
        onCompositionStart={() => {
          composingRef.current = true;
          setIsComposing(true);
        }}
        onCompositionEnd={() => {
          composingRef.current = false;
          setIsComposing(false);
          handleInput();
        }}
        onPaste={(e) => {
          if (readOnly) return;
          // 粘贴处理
          e.preventDefault();
          e.stopPropagation();
          const paste = e.clipboardData.getData("text/plain");
          const el = editorRef.current;
          if (!el) return;
          const sel = getSelectionCharOffsets(el);
          const caret = getCaretOffset(el);
          const start = sel?.start ?? caret;
          const end = sel?.end ?? caret;
          const caretAfter = start + paste.length;
          pendingCaretRef.current = caretAfter;
          const next = trimToWordCount(value.slice(0, start) + paste + value.slice(end), maxCount);
          if (marks.length > 0) {
            onMarksChange(reconcileScriptReplaceMarks(value, next, marks, caretAfter));
          }
          onChange(next);
          closeToolbar();
          closeHoverTip();
        }}
        onKeyDown={(e) => {
          if (readOnly) return;
          onKeyDown?.(e);
        }}
        onMouseUp={updateToolbarFromSelection}
        onSelect={updateToolbarFromSelection}
        onScroll={() => {
          closeToolbar();
          closeHoverTip();
        }}
      />

      {createPortal(
        <AnimatePresence>
          {toolbar && !readOnly && (
            <PronunciationFloatMount
              key={pronunciationToolbarKey(toolbar)}
              anchorKey={pronunciationToolbarKey(toolbar)}
              className="z-[200]"
              left={toolbar.left}
              top={toolbar.top - 8}
              innerInitialY={12}
              innerExitY={8}
              innerUseScale={false}
              springStiffness={420}
            >
              <button
                type="button"
                data-pronunciation-toolbar
                className="flex cursor-pointer items-center gap-1.5 rounded-full border border-[rgba(234,236,240,1)] bg-white px-[6px] py-[5px] text-xs font-medium text-[#101828] shadow-lg transition-colors"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  const selLen = toolbar.end - toolbar.start;
                  if (selLen > MAX_WORD_SELECTION_CHARS) {
                    closeToolbar();
                    toast.default({
                      description: t("agent_creation.input.pronunciation.max_selection_chars"),
                    });
                    return;
                  }
                  openModalForRange(
                    toolbar.start,
                    toolbar.end,
                    getScriptReplaceReadingForRange(marks, toStoredRange(toolbar.start, toolbar.end))
                  );
                }}
              >
                <div className="flex h-full w-full items-center justify-center gap-[6.8px] rounded-lg p-1.5 py-0.5 transition-colors hover:bg-[#f3f3f4] active:bg-[#f3f3f4] text-[rgba(16,24,40,1)]">
                  <SpeakVoiceIcon className="" aria-hidden />
                  <span className="text-sm">{t("agent_creation.input.pronunciation.modify_button")}</span>
                </div>
              </button>
            </PronunciationFloatMount>
          )}
        </AnimatePresence>,
        document.body
      )}

      {createPortal(
        <AnimatePresence>
          {hoverTip && !readOnly && !toolbarOverlapsHoverTip && (
            <PronunciationFloatMount
              key={pronunciationHoverTipKey(hoverTip)}
              anchorKey={pronunciationHoverTipKey(hoverTip)}
              className="z-[250]"
              left={hoverTip.left}
              top={hoverTip.top - 6}
              innerClassName="cursor-pointer flex flex-col items-center gap-0"
              innerInitialY={10}
              innerExitY={6}
              springStiffness={440}
              innerUseScale={false}
              innerMotionProps={{
                onMouseEnter: cancelHoverClear,
                onMouseLeave: clearHoverSoon,
                onMouseDown: (e) => e.preventDefault(),
                onClick: () => {
                  const [a, b] = toHalfOpenRange(hoverTip.mark.indexRange);
                  openModalForRange(a, b, hoverTip.mark.text);
                },
              }}
            >
              <div className="flex items-center gap-1.5 rounded-md border-0 bg-[#1D2939] px-2.5 py-1.5 text-sm text-white shadow-lg">
                <span className="">{hoverTip.reading}</span>
                <EditUnderlineIcon />
              </div>
              <div
                className="h-0 w-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-[#1D2939]"
                aria-hidden
              />
            </PronunciationFloatMount>
          )}
        </AnimatePresence>,
        document.body
      )}

      <ScriptPronunciationModal
        open={modalOpen}
        onOpenChange={(o) => {
          if (!o && modalRange) {
            // 焦点在弹窗内时选区不在 editor 上，getCaretOffset 会返回 0；关闭时把光标留在划词区间末尾
            pendingCaretRef.current = modalRange.end;
          }
          setModalOpen(o);
          if (!o) setModalRange(null);
        }}
        originalText={modalRange ? value.slice(modalRange.start, modalRange.end) : ""}
        initialReading={modalReading}
        onRemove={handleRemovePronunciation}
        onSave={handleSavePronunciation}
      />
    </div>
  );
}
);

const pronunciationFloatOuterTransition = { duration: 0.12 };

const pronunciationFloatInnerSpringBase = {
  type: "spring" as const,
  damping: 26,
  mass: 0.85,
};

type PronunciationFloatMountProps = {
  anchorKey: string;
  className: string;
  left: number;
  top: number;
  innerClassName?: string;
  innerInitialY?: number;
  innerExitY?: number;
  springStiffness?: number;
  /** 为 false 时内层只做位移动画（无 scale），避免文字在缩放时栅格抖动 */
  innerUseScale?: boolean;
  innerMotionProps?: Omit<
    React.ComponentProps<typeof motion.div>,
    "children" | "className" | "initial" | "animate" | "exit" | "transition"
  >;
  children: React.ReactNode;
};

/** 划词工具条 / 读音气泡：外层淡入 + 内层自触发点上弹 */
function PronunciationFloatMount({
  anchorKey,
  className,
  left,
  top,
  innerClassName,
  innerInitialY = 11,
  innerExitY = 7,
  springStiffness = 430,
  innerUseScale = true,
  innerMotionProps,
  children,
}: PronunciationFloatMountProps) {
  const innerInitial = innerUseScale
    ? { y: innerInitialY, scale: 0.94 }
    : { y: innerInitialY };
  const innerAnimate = innerUseScale ? { y: 0, scale: 1 } : { y: 0 };
  const innerExit = innerUseScale
    ? { y: innerExitY, scale: 0.96 }
    : { y: innerExitY };
  const innerTransition = innerUseScale
    ? { ...pronunciationFloatInnerSpringBase, stiffness: springStiffness }
    : { type: "tween" as const, duration: 0.2, ease: [0.22, 1, 0.36, 1] as const };

  return (
    <motion.div
      key={anchorKey}
      className={cn("fixed", className)}
      style={{
        left,
        top,
        transform: "translateX(-50%) translateY(-100%)",
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={pronunciationFloatOuterTransition}
    >
      <motion.div
        className={cn("origin-bottom", innerClassName)}
        initial={innerInitial}
        animate={innerAnimate}
        exit={innerExit}
        transition={innerTransition}
        {...innerMotionProps}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

ScriptMarkedEditor.displayName = "ScriptMarkedEditor";
